import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Radio, TrendingUp, Users, Zap, BarChart2, Eye, ArrowRight, Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const OPAL_LINK = "https://opal.google/app/1ylT6lJVAJIAkOmAjsIS8XPWiBG4qUG51";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/65cb7bb6-9fa6-4ebf-a43f-e4caddb43618/images/048d85b2a44f8c184fb9301cd665030d7495079aea7b27d144166216e22f74a8.png";
const FEATURE_REALTIME = "https://static.prod-images.emergentagent.com/jobs/65cb7bb6-9fa6-4ebf-a43f-e4caddb43618/images/efa331cf4a92530281e7fa963e100a0b25ce62e58e9a9de4c4434941dcec176d.png";
const FEATURE_SENTIMENT = "https://static.prod-images.emergentagent.com/jobs/65cb7bb6-9fa6-4ebf-a43f-e4caddb43618/images/5360ee516afe99753dffee98cdc8e40ed1766db80a02d57bafcfbd9b9237047b.png";
const FEATURE_COMPETITOR = "https://static.prod-images.emergentagent.com/jobs/65cb7bb6-9fa6-4ebf-a43f-e4caddb43618/images/526fdc69ecc5b9f66387c185dcb89dba5b64731f0340d8fa6ce41b9632e3d1bc.png";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`reveal-el ${inView ? "revealed" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl shadow-sm border-b border-[#E7E5E4]/60" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a href="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#C94B7B] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
            <Radio className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-[#1C1917] text-lg tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Brand<span className="text-[#FF6B35]">Radar</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {["Features", "How it Works", "Why Us"].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
              data-testid={`nav-${item.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[#57534E] hover:text-[#1C1917] font-medium text-sm transition-colors duration-200"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {item}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={OPAL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-btn"
            className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 shadow-[0_4px_15px_-4px_rgba(255,107,53,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(255,107,53,0.6)] hover:-translate-y-0.5"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Get Started
          </a>
        </div>

        <button
          className="md:hidden p-2 rounded-lg text-[#57534E] hover:text-[#1C1917]"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#E7E5E4]/60 px-4 py-4 flex flex-col gap-4">
          {["Features", "How it Works", "Why Us"].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[#57534E] font-medium py-1"
              onClick={() => setMenuOpen(false)}
            >
              {item}
            </a>
          ))}
          <a
            href={OPAL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-cta-btn"
            className="bg-[#FF6B35] text-white rounded-full px-6 py-3 text-sm font-semibold text-center mt-1"
            onClick={() => setMenuOpen(false)}
          >
            Get Started
          </a>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-[#FAF8FA]" data-testid="hero-section">
      {/* Background blobs */}
      <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[#FF6B35]/8 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#9D8189]/10 rounded-full blur-[80px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#9D8189]/10 border border-[#9D8189]/20 rounded-full px-4 py-1.5 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
            <span className="text-[#9D8189] text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Real-time Brand Intelligence
            </span>
          </div>

          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#1C1917] tracking-tighter leading-none mb-6 animate-fade-up"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            See Your Brand{" "}
            <span className="text-gradient-hero">Through the Noise.</span>
          </h1>

          <p
            className="text-base lg:text-lg text-[#57534E] leading-relaxed mb-10 max-w-lg animate-fade-up-delay"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Track every mention, decode sentiment in real time, and stay ahead of competitors — all in one intelligent dashboard.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-fade-up-delay-2">
            <a
              href={OPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-8 py-4 font-semibold transition-all duration-300 shadow-[0_8px_20px_-8px_rgba(255,107,53,0.5)] hover:shadow-[0_8px_25px_-5px_rgba(255,107,53,0.6)] hover:-translate-y-0.5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Start Monitoring Free
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#features"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1C1917] border border-[#E7E5E4] hover:bg-[#FAF8FA] hover:border-[#9D8189]/40 rounded-full px-8 py-4 font-semibold transition-all duration-300"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              See How it Works
            </a>
          </div>

          <div className="flex items-center gap-6 mt-10 pt-8 border-t border-[#E7E5E4]/60">
            {[
              { value: "10M+", label: "Mentions tracked daily" },
              { value: "98%", label: "Sentiment accuracy" },
              { value: "<1s", label: "Alert latency" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl font-bold text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {stat.value}
                </div>
                <div className="text-xs text-[#9D8189] mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center justify-center lg:justify-end">
          <div className="relative w-full max-w-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/20 to-[#9D8189]/20 rounded-3xl blur-2xl" />
            <img
              src={HERO_IMG}
              alt="Brand Radar Dashboard"
              data-testid="hero-image"
              className="relative rounded-3xl w-full object-cover shadow-2xl animate-float"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustedBy() {
  const logos = ["Acme Corp", "Nexus Brand", "PulseMedia", "Vertex Labs", "Orbit Co"];
  return (
    <section className="py-12 border-y border-[#E7E5E4]/60 bg-white" data-testid="trusted-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-sm text-[#9D8189] font-medium mb-8 uppercase tracking-widest" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Trusted by modern marketing teams
        </p>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14">
          {logos.map((name) => (
            <div key={name} className="text-[#C8C4C0] font-bold text-lg tracking-tight hover:text-[#9D8189] transition-colors duration-300 cursor-default select-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    {
      icon: <Zap className="w-6 h-6 text-[#FF6B35]" />,
      title: "Real-time Mentions",
      description: "Monitor every brand mention across social media, news, blogs, and forums the instant it happens. Never miss a conversation about your brand again.",
      image: FEATURE_REALTIME,
      testId: "feature-realtime",
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-[#9D8189]" />,
      title: "Sentiment Analysis",
      description: "AI-powered sentiment scoring breaks down how the world feels about your brand — positive, neutral, or negative — with pinpoint accuracy.",
      image: FEATURE_SENTIMENT,
      testId: "feature-sentiment",
    },
    {
      icon: <Eye className="w-6 h-6 text-[#FF6B35]" />,
      title: "Competitor Tracking",
      description: "Keep tabs on your competitors' brand health, campaign performance, and audience reactions side by side with your own metrics.",
      image: FEATURE_COMPETITOR,
      testId: "feature-competitor",
    },
  ];

  return (
    <section id="features" className="py-24 sm:py-32 bg-[#FAF8FA]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-16">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold uppercase tracking-widest mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Core Features
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tighter"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Everything your brand needs<br />to stay ahead.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feat, i) => (
            <Reveal key={feat.testId} delay={i * 100}>
              <div
                data-testid={feat.testId}
                className="bg-white border border-[#E7E5E4]/60 rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-[#9D8189]/8 transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#9D8189]/5 to-[#FF6B35]/5">
                  <img
                    src={feat.image}
                    alt={feat.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="w-10 h-10 rounded-2xl bg-[#FAF8FA] flex items-center justify-center mb-4 border border-[#E7E5E4]/60">
                    {feat.icon}
                  </div>
                  <h3
                    className="text-xl sm:text-2xl font-bold text-[#1C1917] mb-3 tracking-tight"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {feat.title}
                  </h3>
                  <p
                    className="text-sm text-[#57534E] leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {feat.description}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      num: "01",
      icon: <Radio className="w-6 h-6 text-[#FF6B35]" />,
      title: "Connect Your Brand",
      desc: "Add your brand name, keywords, and social handles. Brand Radar instantly begins sweeping the web.",
    },
    {
      num: "02",
      icon: <TrendingUp className="w-6 h-6 text-[#9D8189]" />,
      title: "Track in Real Time",
      desc: "Watch mentions, sentiment scores, and engagement metrics update live across all platforms.",
    },
    {
      num: "03",
      icon: <Users className="w-6 h-6 text-[#FF6B35]" />,
      title: "Act on Intelligence",
      desc: "Receive instant alerts, detailed reports, and actionable insights to respond faster than competitors.",
    },
  ];

  return (
    <section id="how-it-works" className="py-24 sm:py-32 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-20">
          <span className="inline-block text-[#9D8189] text-sm font-semibold uppercase tracking-widest mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            How it Works
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tighter"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Up and running<br />in three steps.
          </h2>
        </Reveal>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {/* connector line */}
          <div className="hidden md:block absolute top-12 left-[calc(16.67%+16px)] right-[calc(16.67%+16px)] h-px bg-gradient-to-r from-[#FF6B35]/30 via-[#9D8189]/30 to-[#FF6B35]/30" />

          {steps.map((step, i) => (
            <Reveal key={step.num} delay={i * 120}>
              <div data-testid={`step-${step.num}`} className="relative text-center md:text-left">
                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FAF8FA] border border-[#E7E5E4]/60 mb-6 shadow-sm md:mb-8">
                  <span className="absolute -top-2.5 -right-2.5 bg-[#FF6B35] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {i + 1}
                  </span>
                  {step.icon}
                </div>
                <h3
                  className="text-xl sm:text-2xl font-bold text-[#1C1917] mb-3 tracking-tight"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm text-[#57534E] leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {step.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const stats = [
    { value: "50+", label: "Data sources monitored" },
    { value: "200+", label: "Brand teams trust us" },
    { value: "10M+", label: "Mentions tracked daily" },
    { value: "< 1s", label: "Real-time alert speed" },
  ];

  return (
    <section id="why-us" className="py-24 sm:py-32 bg-[#FAF8FA]" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <span className="inline-block text-[#FF6B35] text-sm font-semibold uppercase tracking-widest mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Why Brand Radar
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tighter mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Intelligence that moves at the speed of your brand.
            </h2>
            <p
              className="text-base text-[#57534E] leading-relaxed mb-8"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Brand Radar aggregates signals from social media, news outlets, review platforms, and forums into a single, actionable stream. Know what's happening — and why — before your competitors do.
            </p>
            <a
              href={OPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="why-us-cta"
              className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-8 py-4 font-semibold transition-all duration-300 shadow-[0_8px_20px_-8px_rgba(255,107,53,0.5)] hover:shadow-[0_8px_25px_-5px_rgba(255,107,53,0.6)] hover:-translate-y-0.5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Open Brand Radar <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>

          <div className="grid grid-cols-2 gap-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div
                  data-testid={`stat-${i}`}
                  className="bg-[#9D8189]/5 border border-[#9D8189]/10 rounded-2xl p-6 text-center hover:border-[#FF6B35]/20 hover:bg-[#FF6B35]/5 transition-all duration-300"
                >
                  <div
                    className="text-3xl font-bold text-[#FF6B35] mb-2"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {s.value}
                  </div>
                  <div
                    className="text-sm text-[#57534E]"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTABanner() {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="cta-banner-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2D1B1A] via-[#3D2020] to-[#1C1917] px-8 py-16 sm:px-16 sm:py-20 text-center">
            {/* Glows */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF6B35]/20 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#9D8189]/20 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-8">
                <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
                <span className="text-white/80 text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Now live on Opal
                </span>
              </div>

              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter mb-6"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Ready to command your<br />brand presence?
              </h2>

              <p
                className="text-base text-white/70 leading-relaxed mb-10 max-w-xl mx-auto"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Join hundreds of marketing teams already using Brand Radar to stay ahead of the conversation.
              </p>

              <a
                href={OPAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-banner-btn"
                className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-10 py-4 font-semibold text-lg transition-all duration-300 shadow-[0_10px_30px_-8px_rgba(255,107,53,0.6)] hover:shadow-[0_12px_35px_-6px_rgba(255,107,53,0.7)] hover:-translate-y-0.5"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Launch Brand Radar <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#FAF8FA] border-t border-[#E7E5E4]/60 py-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <a href="/" data-testid="footer-logo" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#C94B7B] flex items-center justify-center shadow-sm">
              <Radio className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-[#1C1917] text-lg tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Brand<span className="text-[#FF6B35]">Radar</span>
            </span>
          </a>

          <p className="text-sm text-[#9D8189]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            &copy; {new Date().getFullYear()} BrandRadar. Powered by{" "}
            <a href="https://opal.google" target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline">
              Opal
            </a>
            .
          </p>

          <div className="flex items-center gap-6">
            {["Privacy", "Terms", "Contact"].map((item) => (
              <a
                key={item}
                href={OPAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-${item.toLowerCase()}`}
                className="text-sm text-[#57534E] hover:text-[#1C1917] transition-colors"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAF8FA]">
      <Header />
      <Hero />
      <TrustedBy />
      <Features />
      <HowItWorks />
      <WhyUs />
      <CTABanner />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
